/**
 * Il tetto giornaliero delle funzioni AI per azienda (22/10/2026, `shared/strumenti/tettoAiGiornaliero.ts`): la console
 * lo legge e lo cambia dalla funzione `consoleTettoAiGiornaliero` (il dato lo scrive solo il server), e legge la spesa
 * di oggi da `consumi_ai_giornalieri` (il super utente la può leggere; nessun altro).
 */
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { COLLEZIONE_SPESA_AI_GIORNALIERA, type ConfigTettoAi, type PianoColTetto } from '@shared/strumenti/tettoAiGiornaliero';

export interface SalvataggioTettoAi {
  per_piano?: Partial<Record<PianoColTetto, number | null>>;
  eccezione?: { tenant_id: string; centesimi: number | null };
  eur_per_usd?: number;
}

async function chiama<I, O>(dati: I): Promise<O> {
  const inizio = performance.now();
  try {
    const r = await httpsCallable<I, O>(functions, 'consoleTettoAiGiornaliero')(dati);
    console.info(`[tettoAi] consoleTettoAiGiornaliero ok in ${Math.round(performance.now() - inizio)} ms`);
    return r.data;
  } catch (err) {
    console.error(`[tettoAi] consoleTettoAiGiornaliero fallita dopo ${Math.round(performance.now() - inizio)} ms`, err);
    throw err;
  }
}

export async function leggiTettoAi(): Promise<{ config: ConfigTettoAi | null; motivi?: string[] }> {
  return chiama({ azione: 'leggi' });
}

export async function salvaTettoAi(modifica: SalvataggioTettoAi): Promise<ConfigTettoAi> {
  const r = await chiama<SalvataggioTettoAi & { azione: 'salva' }, { config: ConfigTettoAi }>({ azione: 'salva', ...modifica });
  return r.config;
}

export interface SpesaAiDiOggi {
  tenant_id: string;
  giorno: string;
  microusd: number;
  voci: number;
}

/** Il giorno civile di Roma ('AAAA-MM-GG'): la grande maggioranza delle aziende ha quel fuso. */
export function oggiARoma(adesso: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit' }).format(adesso);
}

/** La spesa AI di oggi di tutte le aziende che hanno un tetto. */
export async function leggiSpesaAiDiOggi(giorno: string = oggiARoma()): Promise<SpesaAiDiOggi[]> {
  const s = await getDocs(query(collection(db, COLLEZIONE_SPESA_AI_GIORNALIERA), where('giorno', '==', giorno)));
  return s.docs.map((d) => {
    const x = d.data();
    return {
      tenant_id: String(x.tenant_id ?? ''),
      giorno: String(x.giorno ?? giorno),
      microusd: typeof x.microusd === 'number' ? x.microusd : 0,
      voci: typeof x.voci === 'number' ? x.voci : 0,
    };
  });
}
